// RegisterBusStand.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { 
  FiSave, 
  FiX, 
  FiMapPin, 
  FiPhone, 
  FiCheckCircle, 
  FiGlobe,
  FiMap,
  FiAlertCircle,
  FiHash,
  FiRefreshCw
} from 'react-icons/fi';
import { ToastContainer, toast } from 'react-toastify';
import BaseUrl from '../../../Url/BaseUrl';

const RegisterBusStand = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    busStandCode: '',
    busStandName: '',
    busStandNameHi: '',
    stateCode: '',
    state: '',
    districtCode: '',
    district: '',
    cityVillageCode: '',
    cityVillage: '',
    latitude: '',
    longitude: '',
    elevation: '',
    status: true,
    busStandContactNumber: { dialCode: '91', number: '' },
    totalPlatforms: 1,
    facilities: ['Waiting Area', 'Restrooms'],
    description: ''
  });

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cityVillages, setCityVillages] = useState([]);
  
  const [loading, setLoading] = useState({
    states: false,
    districts: false,
    cityVillages: false
  });
  
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [busStandCodeStatus, setBusStandCodeStatus] = useState(null);
  const [checkingCode, setCheckingCode] = useState(false);

  const STATES_API = `${BaseUrl.baseurl}States`;
  const DISTRICTS_API = `${BaseUrl.baseurl}Districts/GetDistrictsByStateId`;
  const CITY_VILLAGES_API = `${BaseUrl.baseurl}CityVillages/GetCityVillagesByDistrictCode`;
  const BUS_STANDS_API = `${BaseUrl.baseurl}BusStands`;
  const CHECK_EXISTS_API = `${BaseUrl.baseurl}BusStands/CheckIfExists`;

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    if (formData.stateCode) {
      fetchDistricts(formData.stateCode);
      setFormData(prev => ({
        ...prev,
        districtCode: '',
        district: '',
        cityVillageCode: '',
        cityVillage: ''
      }));
      setDistricts([]);
      setCityVillages([]);
    } else {
      setDistricts([]);
    }
  }, [formData.stateCode]);

  useEffect(() => {
    if (formData.districtCode) {
      fetchCityVillages(formData.districtCode);
      setFormData(prev => ({
        ...prev,
        cityVillageCode: '',
        cityVillage: ''
      }));
      setCityVillages([]);
    } else {
      setCityVillages([]);
    }
  }, [formData.districtCode]);

  const fetchStates = async () => {
    try {
      setLoading(prev => ({ ...prev, states: true }));
      setErrorMessage('');
      const response = await axios.get(STATES_API, { headers: { 'accept': 'text/plain' } });
      setStates(response.data || []);
    } catch (error) {
      console.error('Error fetching states:', error);
      const errorMsg = error.response?.status 
        ? t('serverResponseError', 'Server responded with status {{status}}', { status: error.response.status })
        : error.message;
      setErrorMessage(`${t('failedToLoadStates', 'Failed to load states')}: ${errorMsg}`);
    } finally {
      setLoading(prev => ({ ...prev, states: false }));
    }
  };

  const fetchDistricts = async (stateCode) => {
    if (!stateCode) return;
    try {
      setLoading(prev => ({ ...prev, districts: true }));
      const response = await axios.get(`${DISTRICTS_API}?StateCode=${stateCode}`, {
        headers: { 'accept': 'text/plain' }
      });
      setDistricts(response.data || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      const errorMsg = error.response?.status 
        ? t('serverResponseError', 'Server responded with status {{status}}', { status: error.response.status })
        : error.message;
      setErrorMessage(`${t('failedToLoadDistricts', 'Failed to load districts')}: ${errorMsg}`);
    } finally {
      setLoading(prev => ({ ...prev, districts: false }));
    }
  };

  const fetchCityVillages = async (districtCode) => {
    if (!districtCode) return;
    try {
      setLoading(prev => ({ ...prev, cityVillages: true }));
      const response = await axios.get(`${CITY_VILLAGES_API}?DistrictCode=${districtCode}`, {
        headers: { 'accept': 'text/plain' }
      });
      // ✅ FILTER: Only show villages with status = true (active)
      const activeVillages = (response.data || []).filter(village => village.status === true);
      setCityVillages(activeVillages);
    } catch (error) {
      console.error('Error fetching city villages:', error);
      const errorMsg = error.response?.status 
        ? t('serverResponseError', 'Server responded with status {{status}}', { status: error.response.status })
        : error.message;
      setErrorMessage(`${t('failedToLoadCityVillages', 'Failed to load city villages')}: ${errorMsg}`);
    } finally {
      setLoading(prev => ({ ...prev, cityVillages: false }));
    }
  };

  const checkBusStandCodeExists = async (code) => {
    if (!code) return false;
    try {
      const response = await axios.get(`${CHECK_EXISTS_API}?id=${code}`, {
        headers: { accept: 'text/plain' }
      });
      return response.data;
    } catch (err) {
      console.error('Check existence failed:', err);
      return false;
    }
  };

  const generateStandCode = async () => {
    let attempts = 0;
    const maxAttempts = 10;
    let newCode = null;
    let isAvailable = false;

    while (attempts < maxAttempts && !isAvailable) {
      const prefix = 'BS';
      const randomNum = Math.floor(Math.random() * 9000) + 1000;
      const candidate = `${prefix}${randomNum}`;
      const exists = await checkBusStandCodeExists(candidate);
      if (!exists) {
        newCode = candidate;
        isAvailable = true;
        break;
      }
      attempts++;
    }

    if (isAvailable && newCode) {
      setFormData(prev => ({ ...prev, busStandCode: newCode }));
      setBusStandCodeStatus('available');
      toast.success(`Bus Stand Code ${newCode} is available!`,{ style: {
        background: '#ffffff',
        color: '#1f2937',
        border: '1px solid #e5e7eb'
      }});
    } else {
      toast.error('Could not generate a unique bus stand code. Please try again or enter manually.');
    }
    
    if (validationErrors.busStandCode) {
      setValidationErrors(prev => ({ ...prev, busStandCode: '' }));
    }
  };

  const checkBusStandCodeAvailability = async () => {
    const code = formData.busStandCode;
    if (!code.trim()) {
      toast.error(t('enterBusStandCodeToCheck', 'Please enter a bus stand code to check.'));
      return;
    }
    setCheckingCode(true);
    try {
      const exists = await checkBusStandCodeExists(code);
      if (exists) {
        setBusStandCodeStatus('taken');
        toast.error(t('busStandCodeExists', 'Bus stand code already exists.'));
      } else {
        setBusStandCodeStatus('available');
        toast.success(t('busStandCodeAvailable', 'Bus stand code is available!'),{ style: {
          background: '#ffffff',
          color: '#1f2937',
          border: '1px solid #e5e7eb'
        }});
      }
    } catch (err) {
      toast.error(t('checkAvailabilityFailed', 'Failed to check availability. Please try again.'));
      setBusStandCodeStatus(null);
    } finally {
      setCheckingCode(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.busStandCode.trim()) {
      errors.busStandCode = t('busStandCodeRequired', 'Bus stand code is required');
    }
    if (busStandCodeStatus === 'taken') {
      errors.busStandCode = t('busStandCodeTaken', 'This code is already taken');
    }
    
    if (!formData.busStandName.trim()) {
      errors.busStandName = t('busStandNameRequired', 'Bus stand name is required');
    }
    
    if (!formData.stateCode) {
      errors.stateCode = t('stateRequired', 'State is required');
    }
    
    if (!formData.districtCode) {
      errors.districtCode = t('districtRequired', 'District is required');
    }
    
    if (!formData.cityVillageCode) {
      errors.cityVillageCode = t('cityVillageCodeRequired', 'City/Village is required');
    }
    
    if (!formData.latitude || formData.latitude.trim() === '') {
      errors.latitude = t('latitudeRequired', 'Latitude is required');
    } else {
      const lat = parseFloat(formData.latitude);
      if (isNaN(lat)) {
        errors.latitude = t('validLatitudeNumber', 'Latitude must be a valid number');
      } else if (lat < -90 || lat > 90) {
        errors.latitude = t('validLatitudeRange', 'Latitude must be between -90 and 90');
      }
    }
    
    if (!formData.longitude || formData.longitude.trim() === '') {
      errors.longitude = t('longitudeRequired', 'Longitude is required');
    } else {
      const lng = parseFloat(formData.longitude);
      if (isNaN(lng)) {
        errors.longitude = t('validLongitudeNumber', 'Longitude must be a valid number');
      } else if (lng < -180 || lng > 180) {
        errors.longitude = t('validLongitudeRange', 'Longitude must be between -180 and 180');
      }
    }
    
    if (formData.busStandContactNumber.number && 
        !/^\d{10,15}$/.test(formData.busStandContactNumber.number)) {
      errors.busStandContactNumber = t('invalidContactNumberFormat', 'Invalid contact number format');
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      if (name === 'stateCode') {
        const selectedState = states.find(state => state.stateCode === value);
        updatedData.state = selectedState ? selectedState.stateName : '';
        updatedData.districtCode = '';
        updatedData.district = '';
        updatedData.cityVillageCode = '';
        updatedData.cityVillage = '';
      }
      
      if (name === 'districtCode') {
        const selectedDistrict = districts.find(district => district.districtCode === value);
        updatedData.district = selectedDistrict ? selectedDistrict.districtName : '';
        updatedData.cityVillageCode = '';
        updatedData.cityVillage = '';
      }
      
      if (name === 'cityVillageCode') {
        const selectedCityVillage = cityVillages.find(cv => cv.cityVillageCode === value);
        updatedData.cityVillage = selectedCityVillage ? selectedCityVillage.cityVillageName : '';
      }
      
      return updatedData;
    });
    
    if (name === 'busStandCode') {
      setBusStandCodeStatus(null);
    }
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (errorMessage) setErrorMessage('');
  };

  const handleContactNumberChange = (field, value) => {
    if (field === 'dialCode') {
      setFormData(prev => ({
        ...prev,
        busStandContactNumber: {
          ...prev.busStandContactNumber,
          dialCode: value
        }
      }));
    } else if (field === 'number') {
      const numericValue = value.replace(/\D/g, '').slice(0, 15);
      setFormData(prev => ({
        ...prev,
        busStandContactNumber: {
          ...prev.busStandContactNumber,
          number: numericValue
        }
      }));
    }
    if (validationErrors.busStandContactNumber) {
      setValidationErrors(prev => ({ ...prev, busStandContactNumber: '' }));
    }
  };

  const handleCoordinateChange = (e) => {
    const { name, value } = e.target;
    if (value === '') {
      setFormData(prev => ({ ...prev, [name]: '' }));
      return;
    }
    const cleanedValue = value.replace(/[^\d.-]/g, '');
    if (cleanedValue.includes('-') && cleanedValue.indexOf('-') !== 0) return;
    const decimalParts = cleanedValue.split('.');
    if (decimalParts.length > 1 && decimalParts[1].length > 6) return;
    setFormData(prev => ({ ...prev, [name]: cleanedValue }));
    if (validationErrors[name]) setValidationErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // if (!validateForm()) {
    //   setErrorMessage(t('fixValidationErrors', 'Please fix the validation errors before submitting.'));
    //   return;
    // }
    
    // if (busStandCodeStatus !== 'available') {
    //   setErrorMessage(t('busStandCodeNotAvailable', 'Please ensure the bus stand code is available.'));
    //   return;
    // }
    
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    const latitude = parseFloat(formData.latitude);
    const longitude = parseFloat(formData.longitude);
    
    const payload = {
      busStandCode: formData.busStandCode.trim(),
      busStandName: formData.busStandName.trim(),
      hindi: formData.busStandNameHi?.trim() || null,
      cityVillageCode: formData.cityVillageCode,
      latitude: parseFloat(latitude.toFixed(6)),
      longitude: parseFloat(longitude.toFixed(6)),
      elevation: formData.elevation?.trim() || null,
      status: formData.status,
      busStandContactNumber: formData.busStandContactNumber.number 
        ? `+${formData.busStandContactNumber.dialCode}${formData.busStandContactNumber.number}`
        : null
    };
    
    Object.keys(payload).forEach(key => {
      if (payload[key] === null || payload[key] === '') {
        delete payload[key];
      }
    });

    try {
      console.log('Submitting payload:', payload);
      const response = await axios.post(BUS_STANDS_API, payload, {
        headers: {
          'accept': 'text/plain',
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response:', response);
      toast.success("Bus Stop/Stand Registered successfully",{ style: {
        background: '#ffffff',
        color: '#1f2937',
        border: '1px solid #e5e7eb'
      }});
      setSuccessMessage(t('busStandRegisteredSuccess', 'Bus stand "{{name}}" registered successfully!', {
        name: formData.busStandName
      }));
      
      setTimeout(() => {
        setFormData({
          busStandCode: '',
          busStandName: '',
          busStandNameHi: '',
          stateCode: '',
          state: '',
          districtCode: '',
          district: '',
          cityVillageCode: '',
          cityVillage: '',
          latitude: '',
          longitude: '',
          elevation: '',
          status: true,
          busStandContactNumber: { dialCode: '91', number: '' },
          totalPlatforms: 1,
          facilities: ['Waiting Area', 'Restrooms'],
          description: ''
        });
        setBusStandCodeStatus(null);
        navigate('/route-finder-panel');
      }, 3000);
      
    } catch (error) {
      console.error('Registration failed:', error);
      if (error.response) {
        if (error.response.status === 409) {
          setErrorMessage(t('busStandCodeExists', 'Bus stand code already exists. Please use a different code.'));
          setBusStandCodeStatus('taken');
        } else if (error.response.status === 400) {
          setErrorMessage(t('invalidData', 'Invalid data. Please check your inputs.'));
        } else if (error.response.status === 404) {
          setErrorMessage(t('cityVillageNotFound', 'City/Village not found. Please select a valid location.'));
        } else {
          setErrorMessage(t('serverError', 'Server error: {{status}} - {{statusText}}', {
            status: error.response.status,
            statusText: error.response.statusText
          }));
        }
      } else if (error.request) {
        setErrorMessage(t('networkError', 'Network error. Please check your connection and try again.'));
      } else {
        setErrorMessage(t('unexpectedError', 'An unexpected error occurred. Please try again.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/route-finder-panel');
  };

  return (
    <div className="form-container">
      <ToastContainer/>
      <div className="form-header">
        <button className="btn-back" onClick={handleCancel}>
          ← {t('back', 'Back')}
        </button>
        <p>{t('fillDetailsBelow', 'Fill in the details below to register a new bus stand')}</p>
      </div>

      {errorMessage && (
        <div className="alert alert-error">
          {errorMessage}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <FiCheckCircle /> {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="form">
        <div className="form-section">
          <h3>{t('basicInformation', 'Basic Information')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="busStandCode">
                {t('busStandCodeLabel', 'Bus Stand Code')} *
              </label>
              <div className="input-with-button">
                <input
                  type="text"
                  id="busStandCode"
                  name="busStandCode"
                  value={formData.busStandCode}
                  onChange={handleChange}
                  placeholder={t('busStandCodePlaceholder', 'e.g., BS001')}
                  className={`${validationErrors.busStandCode ? 'error' : ''} ${
                    busStandCodeStatus === 'available' ? 'success' : ''
                  } ${busStandCodeStatus === 'taken' ? 'error' : ''}`}
                  disabled={isSubmitting}
                  required
                />
                <button
                  type="button"
                  className="btn-generate"
                  onClick={generateStandCode}
                  disabled={isSubmitting}
                >
                  <FiHash /> {t('generate', 'Generate')}
                </button>
                <button
                  type="button"
                  className="btn-check"
                  onClick={checkBusStandCodeAvailability}
                  disabled={isSubmitting || checkingCode || !formData.busStandCode.trim()}
                >
                  {checkingCode ? <FiRefreshCw className="spin" /> : <FiCheckCircle />}
                  {t('check', 'Check')}
                </button>
              </div>
              {busStandCodeStatus === 'available' && (
                <div className="status-message success">
                  <FiCheckCircle /> {t('codeAvailable', 'Code is available')}
                </div>
              )}
              {busStandCodeStatus === 'taken' && (
                <div className="status-message error">
                  <FiAlertCircle /> {t('codeTaken', 'Code already exists')}
                </div>
              )}
              {validationErrors.busStandCode && (
                <span className="error-text">{validationErrors.busStandCode}</span>
              )}
              <div className="hint">{t('busStandCodeHint', 'Alphanumeric code for the bus stand')}</div>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="busStandName">
              {t('busStandNameLabel', 'Bus Stand Name (English)')} *
            </label>
            <input
              type="text"
              id="busStandName"
              name="busStandName"
              value={formData.busStandName}
              onChange={handleChange}
              placeholder={t('busStandNamePlaceholder', 'e.g., Central Bus Terminal')}
              className={validationErrors.busStandName ? 'error' : ''}
              disabled={isSubmitting}
              required
            />
            {validationErrors.busStandName && (
              <span className="error-text">{validationErrors.busStandName}</span>
            )}
            <div className="hint">{t('busStandNameHint', 'Full name of the bus stand')}</div>
          </div>

          <div className="form-group">
            <label htmlFor="busStandNameHi">
              {t('busStandNameHiLabel', 'Bus Stand Name (Hindi)')}
            </label>
            <input
              type="text"
              id="busStandNameHi"
              name="busStandNameHi"
              value={formData.busStandNameHi}
              onChange={handleChange}
              placeholder={t('busStandNameHiPlaceholder', 'e.g., सेंट्रल बस टर्मिनल')}
              disabled={isSubmitting}
            />
            <div className="hint">{t('busStandNameHiHint', 'Hindi name of the bus stand (optional)')}</div>
          </div>
        </div>

        <div className="form-section">
          <h3>{t('locationDetails', 'Location Details')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="stateCode">
                <FiGlobe /> {t('state', 'State')} *
                {loading.states && <span className="loading-indicator"><div className="spinner-tiny"></div></span>}
              </label>
              <select
                id="stateCode"
                name="stateCode"
                value={formData.stateCode}
                onChange={handleChange}
                className={validationErrors.stateCode ? 'error' : ''}
                disabled={loading.states || isSubmitting}
                required
              >
                <option value="">{t('selectState', 'Select State')}</option>
                {states.map(state => (
                  <option key={state.stateCode} value={state.stateCode}>
                    {state.stateName}
                  </option>
                ))}
              </select>
              {validationErrors.stateCode && (
                <span className="error-text">{validationErrors.stateCode}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="districtCode">
                <FiMap /> {t('district', 'District')} *
                {loading.districts && <span className="loading-indicator"><div className="spinner-tiny"></div></span>}
              </label>
              <select
                id="districtCode"
                name="districtCode"
                value={formData.districtCode}
                onChange={handleChange}
                className={validationErrors.districtCode ? 'error' : ''}
                disabled={loading.districts || isSubmitting || !formData.stateCode}
                required
              >
                <option value="">{t('selectDistrict', 'Select District')}</option>
                {districts.map(district => (
                  <option key={district.districtCode} value={district.districtCode}>
                    {district.districtName}
                  </option>
                ))}
              </select>
              {validationErrors.districtCode && (
                <span className="error-text">{validationErrors.districtCode}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="cityVillageCode">
                <FiMapPin /> {t('selectCityVillageLabel', 'Select City/Village')} *
                {loading.cityVillages && <span className="loading-indicator"><div className="spinner-tiny"></div></span>}
              </label>
              <select
                id="cityVillageCode"
                name="cityVillageCode"
                value={formData.cityVillageCode}
                onChange={handleChange}
                className={validationErrors.cityVillageCode ? 'error' : ''}
                disabled={loading.cityVillages || isSubmitting || !formData.districtCode}
                required
              >
                <option value="">{t('selectCityVillage', 'Select City/Village')}</option>
                {cityVillages.map(city => (
                  <option key={city.cityVillageCode} value={city.cityVillageCode}>
                    {city.cityVillageName}
                  </option>
                ))}
              </select>
              {validationErrors.cityVillageCode && (
                <span className="error-text">{validationErrors.cityVillageCode}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="latitude">
                {t('latitudeLabel', 'Latitude')} *
              </label>
              <input
                type="text"
                id="latitude"
                name="latitude"
                value={formData.latitude}
                onChange={handleCoordinateChange}
                placeholder={t('latitudePlaceholder', 'e.g., 28.613939')}
                className={validationErrors.latitude ? 'error' : ''}
                disabled={isSubmitting}
                required
              />
              {validationErrors.latitude && (
                <span className="error-text">{validationErrors.latitude}</span>
              )}
              <div className="hint">{t('latitudeHint', 'Between -90 and 90 (up to 6 decimal places)')}</div>
            </div>

            <div className="form-group">
              <label htmlFor="longitude">
                {t('longitudeLabel', 'Longitude')} *
              </label>
              <input
                type="text"
                id="longitude"
                name="longitude"
                value={formData.longitude}
                onChange={handleCoordinateChange}
                placeholder={t('longitudePlaceholder', 'e.g., 77.231028')}
                className={validationErrors.longitude ? 'error' : ''}
                disabled={isSubmitting}
                required
              />
              {validationErrors.longitude && (
                <span className="error-text">{validationErrors.longitude}</span>
              )}
              <div className="hint">{t('longitudeHint', 'Between -180 and 180 (up to 6 decimal places)')}</div>
            </div>

            <div className="form-group">
              <label htmlFor="elevation">
                {t('elevationLabel', 'Elevation (Optional)')}
              </label>
              <input
                type="text"
                id="elevation"
                name="elevation"
                value={formData.elevation}
                onChange={handleChange}
                placeholder={t('elevationPlaceholder', 'e.g., 14m above sea level')}
                disabled={isSubmitting}
              />
              <div className="hint">{t('elevationHint', 'Height above sea level')}</div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>{t('contactInformation', 'Contact Information')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="busStandContactNumber">
                <FiPhone /> {t('contactNumberLabel', 'Contact Number (Optional)')}
              </label>
              <div className="phone-input-wrapper">
                <select
                  value={formData.busStandContactNumber.dialCode}
                  onChange={(e) => handleContactNumberChange('dialCode', e.target.value)}
                  className="country-code-select"
                  disabled={isSubmitting}
                >
                  <option value="91">🇮🇳 +91 (India)</option>
                  <option value="1">🇺🇸 +1 (USA/Canada)</option>
                  <option value="44">🇬🇧 +44 (UK)</option>
                  <option value="61">🇦🇺 +61 (Australia)</option>
                  <option value="971">🇦🇪 +971 (UAE)</option>
                  <option value="65">🇸🇬 +65 (Singapore)</option>
                  <option value="60">🇲🇾 +60 (Malaysia)</option>
                  <option value="64">🇳🇿 +64 (New Zealand)</option>
                  <option value="49">🇩🇪 +49 (Germany)</option>
                  <option value="33">🇫🇷 +33 (France)</option>
                  <option value="81">🇯🇵 +81 (Japan)</option>
                  <option value="86">🇨🇳 +86 (China)</option>
                  <option value="82">🇰🇷 +82 (South Korea)</option>
                  <option value="966">🇸🇦 +966 (Saudi Arabia)</option>
                  <option value="974">🇶🇦 +974 (Qatar)</option>
                  <option value="965">🇰🇼 +965 (Kuwait)</option>
                  <option value="968">🇴🇲 +968 (Oman)</option>
                  <option value="880">🇧🇩 +880 (Bangladesh)</option>
                  <option value="94">🇱🇰 +94 (Sri Lanka)</option>
                  <option value="977">🇳🇵 +977 (Nepal)</option>
                  <option value="92">🇵🇰 +92 (Pakistan)</option>
                  <option value="93">🇦🇫 +93 (Afghanistan)</option>
                </select>
                <input
                  type="tel"
                  id="busStandContactNumber"
                  value={formData.busStandContactNumber.number}
                  onChange={(e) => handleContactNumberChange('number', e.target.value)}
                  placeholder={t('contactNumberPlaceholder', 'e.g., 9876543210')}
                  className={validationErrors.busStandContactNumber ? 'error' : ''}
                  disabled={isSubmitting}
                  maxLength="15"
                />
              </div>
              {validationErrors.busStandContactNumber && (
                <span className="error-text">{validationErrors.busStandContactNumber}</span>
              )}
              <div className="hint">{t('contactNumberHint', 'Select country code and enter phone number')}</div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>{t('statusSettings', 'Status Settings')}</h3>
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="status"
                checked={formData.status}
                onChange={handleChange}
                disabled={isSubmitting}
              />
              <span className="checkbox-custom"></span>
              <span className="checkbox-text">
                {formData.status ? t('active', 'Active') : t('inactive', 'Inactive')} {t('status', 'Status')}
              </span>
            </label>
            <p className="hint">
              {formData.status 
                ? t('activeStatusHint', 'Active bus stands will be visible and available for bus services') 
                : t('inactiveStatusHint', 'Inactive bus stands will be hidden from active bus services')}
            </p>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            <FiX /> {t('cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={isSubmitting || busStandCodeStatus !== 'available'}
          >
            {isSubmitting ? (
              <>
                <div className="spinner-small"></div>
                {t('registering', 'Registering...')}
              </>
            ) : (
              <>
                <FiSave /> {t('registerBusStand', 'Register Bus Stand')}
              </>
            )}
          </button>
        </div>
      </form>

      <style jsx>{`
        .form-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 2rem;
        }

        .form-header {
          margin-bottom: 2rem;
        }

        .form-header h1 {
          font-size: 2rem;
          margin-bottom: 0.5rem;
          color: #333;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .form-header p {
          color: #666;
          font-size: 1rem;
        }

        .btn-back {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 0.5rem 1rem;
          cursor: pointer;
          margin-bottom: 1rem;
          transition: all 0.2s;
        }

        .btn-back:hover {
          background: #e9ecef;
          border-color: #adb5bd;
          transform: translateX(-2px);
        }

        .form-section {
          margin-bottom: 2.5rem;
          padding: 1.5rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .form-section h3 {
          margin: 0 0 1.5rem 0;
          color: #333;
          font-size: 1.2rem;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid #f0f0f0;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          font-weight: 500;
          color: #495057;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .input-with-button {
          display: flex;
          gap: 8px;
        }

        .input-with-button input {
          flex: 1;
        }

        .btn-generate, .btn-check {
          padding: 8px 12px;
          background-color: #f0f0f0;
          color: #333;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          white-space: nowrap;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        .btn-generate:hover:not(:disabled), .btn-check:hover:not(:disabled) {
          background-color: #e0e0e0;
        }

        .btn-generate:disabled, .btn-check:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .status-message {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.85rem;
          padding: 0.25rem 0;
        }
        .status-message.success {
          color: #10b981;
        }
        .status-message.error {
          color: #dc2626;
        }

        .form-input.success {
          border-color: #10b981;
          background-color: #f0fdf4;
        }

        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .phone-input-wrapper {
          display: flex;
          gap: 0.5rem;
        }

        .country-code-select {
          width: 140px;
          padding: 0.75rem 1rem;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: all 0.2s;
          background: #f8f9fa;
          cursor: pointer;
        }

        .country-code-select:focus {
          outline: none;
          border-color: #00BCD4;
          background: white;
          box-shadow: 0 0 0 3px rgba(0, 188, 212, 0.1);
        }

        .checkbox-group {
          margin-top: 1rem;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          cursor: pointer;
          user-select: none;
        }

        .checkbox-label input[type="checkbox"] {
          display: none;
        }

        .checkbox-custom {
          width: 20px;
          height: 20px;
          border: 2px solid #dee2e6;
          border-radius: 4px;
          position: relative;
          transition: all 0.2s;
        }

        .checkbox-label input[type="checkbox"]:checked + .checkbox-custom {
          background: #4CAF50;
          border-color: #4CAF50;
        }

        .checkbox-label input[type="checkbox"]:checked + .checkbox-custom::after {
          content: '✓';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-size: 0.8rem;
          font-weight: bold;
        }

        .checkbox-text {
          font-size: 1rem;
          color: #333;
          font-weight: 500;
        }

        .loading-indicator {
          display: inline-flex;
          margin-left: 8px;
        }

        .spinner-tiny {
          width: 12px;
          height: 12px;
          border: 2px solid rgba(102, 126, 234, 0.3);
          border-radius: 50%;
          border-top-color: #667eea;
          animation: spin 0.8s linear infinite;
        }

        .spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 0.8s linear infinite;
        }

        .form-group input,
        .form-group select {
          padding: 0.75rem 1rem;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: all 0.2s;
          background: #f8f9fa;
          font-family: inherit;
        }

        .form-group select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23333' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 1rem center;
          background-size: 16px;
          padding-right: 2.5rem;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #00BCD4;
          background: white;
          box-shadow: 0 0 0 3px rgba(0, 188, 212, 0.1);
        }

        .form-group input.error,
        .form-group select.error {
          border-color: #dc3545;
          background: #fff5f5;
        }

        .error-text {
          color: #dc3545;
          font-size: 0.85rem;
          margin-top: 0.25rem;
        }

        .hint {
          color: #6c757d;
          font-size: 0.85rem;
          margin-top: 0.25rem;
          font-style: italic;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          padding-top: 2rem;
          margin-top: 2rem;
          border-top: 1px solid #dee2e6;
        }

        .btn-secondary, .btn-primary {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          border: none;
          font-size: 0.95rem;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #5a6268;
        }

        .btn-primary {
          background: linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 100%);
          color: black;
        }

        .btn-primary:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .btn-secondary:disabled,
        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .alert {
          padding: 1rem 1.5rem;
          border-radius: 8px;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from {
            transform: translateY(-10px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .alert-error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .alert-success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        @media (max-width: 768px) {
          .form-container {
            padding: 1rem;
          }

          .form-section {
            padding: 1rem;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .input-with-button {
            flex-direction: column;
          }

          .btn-generate, .btn-check {
            width: 100%;
          }

          .phone-input-wrapper {
            flex-direction: column;
          }

          .country-code-select {
            width: 100%;
          }

          .form-actions {
            flex-direction: column;
          }

          .btn-secondary, .btn-primary {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default RegisterBusStand;